/**
 * The union types the database stores as plain strings.
 *
 * Aurora DSQL has no Postgres enum types, so the schema uses `String` columns
 * (see packages/db/prisma/schema.prisma). That makes these schemas the *only*
 * thing standing between a typo and a bad row — every write path must parse
 * through them.
 */

import { z } from 'zod';

/**
 * `owner` — full control of the garage. `crew` — maintenance actions only.
 * The split is enforced per-route in the API and mirrored in the UI (§2, §6).
 */
export const role = z.enum(['owner', 'crew']);
export type Role = z.infer<typeof role>;

/**
 * The permission ladder a route declares a minimum for. `member` is not a
 * stored role — it means "owner or crew", i.e. anyone in the garage. Public
 * readers hold no membership at all and never reach a role check.
 */
export const MIN_ROLES = ['member', 'crew', 'owner'] as const;
export type MinRole = (typeof MIN_ROLES)[number];

/** A street car accrues miles; a strip-only car only ever accrues passes. */
export const useType = z.enum(['street', 'strip']);
export type UseType = z.infer<typeof useType>;

export const ownerType = z.enum(['car', 'driver']);
export type OwnerType = z.infer<typeof ownerType>;

export const triggerType = z.enum(['mileage', 'time', 'passes', 'events', 'inspect']);
export type TriggerType = z.infer<typeof triggerType>;

export const triggerUnit = z.enum(['mi', 'days', 'weeks', 'months', 'passes', 'events']);
export type TriggerUnit = z.infer<typeof triggerUnit>;

export const system = z.enum(['Engine', 'Power adder', 'Fuel', 'Driveline', 'Brakes', 'Chassis', 'Safety']);
export type System = z.infer<typeof system>;

/** `planned` carries estimates that feed the forecast; `actual` is what was run. */
export const eventStatus = z.enum(['planned', 'actual']);
export type EventStatus = z.infer<typeof eventStatus>;

/** The reminder ladder. One row per (item, kind) guarantees each fires once (§6). */
export const notificationKind = z.enum(['exp_60', 'exp_30']);
export type NotificationKind = z.infer<typeof notificationKind>;

export const status = z.enum(['good', 'due', 'over']);
export type Status = z.infer<typeof status>;

/**
 * Which unit pairs with which trigger type. A "time" trigger measured in
 * `passes` is nonsense the engine throws on, so it is rejected at the edge.
 */
export const UNITS_BY_TRIGGER: Record<TriggerType, readonly TriggerUnit[]> = {
  mileage: ['mi'],
  time: ['days', 'weeks', 'months'],
  passes: ['passes'],
  events: ['events'],
  inspect: ['days'],
};
